import React from 'react';
import { styled } from 'baseui';
import { useAtom, useAtomValue } from 'jotai';
import { numFramesAtom } from '../../../labelling-v2/atoms/annotation-atoms';
import { frameRateAtom } from '../../../labelling-v2/atoms/file-atoms';
import { isVideoLabelingM4EnabledAtom } from '../../../labelling-v2/atoms/m4-atoms';
import { selectedVideoIndexAtom } from '../../../labelling-v2/atoms/multi-video-atoms';
import { timeDisplayModeAtom } from '../../../labelling-v2/atoms/tool-atoms';
import { currentFrameAtom } from '../../../labelling-v2/atoms/video-playback-atoms';
import { FramesControls } from '../../../labelling-v2/components/frames-control-panel/frames-controls';
import { FramesControlsM4 } from '../../../labelling-v2/components/frames-control-panel/frames-controls-m4';
import { useMediaState, useMediaStateMediaElement } from '../../providers/hooks/use-media-state';
import { useMediaStateLite, useMediaStateLiteMediaElement } from '../../providers/hooks/use-media-state-lite';
import type { FRAME_CONTROL_MODE } from './constants';
type PropsT = {
  onFrameChange: (frame: number) => void;
  isTimelineExpanded: boolean;
  onIsTimelineExpandedChange: (isExpanded: boolean) => void;
  isVideoPlayer: boolean;
  isDisabled?: boolean;
  mode: (typeof FRAME_CONTROL_MODE)[keyof typeof FRAME_CONTROL_MODE];
};
const Container = styled('div', () => ({
  width: '100%'
}));
export function FramesControlsContainer({
  onFrameChange,
  isDisabled,
  mode,
  isTimelineExpanded,
  onIsTimelineExpandedChange,
  isVideoPlayer
}: PropsT) {
  const {
    isPlaying,
    onPlayStatusChange,
    playSpeed,
    onPlaySpeedChange,
    mediaFrameLoadStatus,
    mediaVolume,
    onMediaVolumeChange
  } = useMediaState();
  const {
    onPlayStatusChange: onPlayStatusChangeLite,
    isPlaying: isPlayingLite,
    playSpeed: playSpeedLite,
    mediaVolume: mediaVolumeLite,
    onMediaVolumeChange: onMediaVolumeChangeLite,
    currentFrame: currentFrameLite,
    maxFrames: maxFramesLite,
    onPlaySpeedChange: onPlaySpeedChangeLite
  } = useMediaStateLite();
  const currentFrame = useAtomValue(currentFrameAtom);
  const maxFrames = useAtomValue(numFramesAtom);
  const frameRate = useAtomValue(frameRateAtom);
  const selectedVideoIndex = useAtomValue(selectedVideoIndexAtom);
  const isVideoLabelingM4Enabled = useAtomValue(isVideoLabelingM4EnabledAtom);
  const isMultiVideoIndex = selectedVideoIndex !== 0;
  const primaryMediaElement = useMediaStateMediaElement();
  const secondaryMediaElement = useMediaStateLiteMediaElement();
  const mediaElement = isMultiVideoIndex ? secondaryMediaElement : primaryMediaElement;
  const [timeDisplayMode, setTimeDisplayMode] = useAtom(timeDisplayModeAtom);
  const resolvedCurrentFrame = isMultiVideoIndex ? currentFrameLite : currentFrame;
  const resolvedMaxFrames = isMultiVideoIndex ? maxFramesLite : maxFrames;
  const resolvedOnFrameChange = onFrameChange;
  const commonFrameControlsProps = {
    isPlaying: isMultiVideoIndex ? isPlayingLite : isPlaying,
    togglePlayback: isMultiVideoIndex ? onPlayStatusChangeLite : onPlayStatusChange,
    playSpeed: isMultiVideoIndex ? playSpeedLite : playSpeed,
    setPlaySpeed: isMultiVideoIndex ? onPlaySpeedChangeLite : onPlaySpeedChange,
    mediaVolume: isMultiVideoIndex ? mediaVolumeLite : mediaVolume,
    onMediaVolumeChange: isMultiVideoIndex ? onMediaVolumeChangeLite : onMediaVolumeChange,
    currentFrame: resolvedCurrentFrame,
    maxFrames: resolvedMaxFrames
  };
  const playback = {
    isPlaying: commonFrameControlsProps.isPlaying,
    togglePlayback: commonFrameControlsProps.togglePlayback,
    currentFrame: resolvedCurrentFrame,
    maxFrames: resolvedMaxFrames,
    imageLoadStatus: mediaFrameLoadStatus,
    onFrameChange: resolvedOnFrameChange,
    isVideoPlayer,
    mediaElement
  };
  const media = {
    playSpeed: commonFrameControlsProps.playSpeed,
    setPlaySpeed: commonFrameControlsProps.setPlaySpeed,
    mediaVolume: commonFrameControlsProps.mediaVolume,
    onMediaVolumeChange: commonFrameControlsProps.onMediaVolumeChange,
    timeDisplayMode,
    setTimeDisplayMode,
    frameRate
  };
  return <Container>
      {isVideoLabelingM4Enabled ? <FramesControlsM4 playback={playback} media={media} isDisabled={isDisabled} isMultiVideoIndex={isMultiVideoIndex} /> : <FramesControls {...commonFrameControlsProps} timeDisplayMode={timeDisplayMode} setTimeDisplayMode={setTimeDisplayMode} imageLoadStatus={mediaFrameLoadStatus} onFrameChange={onFrameChange} frameRate={frameRate} mode={mode} isTimelineExpanded={isTimelineExpanded} onIsTimelineExpandedChange={onIsTimelineExpandedChange} isVideoPlayer={isVideoPlayer} isDisabled={isDisabled} mediaElement={mediaElement} />}
    </Container>;
}